import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import LandingHeader from '../Components/LandingHeader';
import Footer from '../Components/Footer';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email');
  const [emailInput, setEmailInput] = useState(email || '');
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    
    const emailToUnsubscribe = emailInput.trim();
    
    if (!emailToUnsubscribe) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToUnsubscribe)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsUnsubscribing(true);
    try {
      const response = await HttpInterceptor.post('/api/newsletter/unsubscribe', {
        email: emailToUnsubscribe,
      });
      
      toast.success(response.data.message || 'Successfully unsubscribed');
      setIsUnsubscribed(true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to unsubscribe. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUnsubscribing(false);
    }
  };

  return (
    <>
      <LandingHeader />
      <main className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
            {isUnsubscribed ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaCheckCircle className="text-green-600 text-3xl" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Successfully Unsubscribed</h1>
                <p className="text-gray-600 mb-6">
                  You have been successfully unsubscribed from Yogvital newsletter.
                  You will no longer receive newsletter emails from us.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                >
                  Return to Home
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaEnvelope className="text-red-600 text-3xl" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">Unsubscribe from Newsletter</h1>
                  <p className="text-gray-600">
                    We're sorry to see you go! Enter your email address below to unsubscribe from our newsletter.
                  </p>
                </div>

                <form onSubmit={handleUnsubscribe} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="your@email.com"
                      required
                      disabled={isUnsubscribing}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isUnsubscribing || !emailInput.trim()}
                    className="w-full px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isUnsubscribing ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Unsubscribing...
                      </>
                    ) : (
                      <>
                        <FaTimesCircle />
                        Unsubscribe
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> If you change your mind, you can always resubscribe by entering your email in the newsletter section on our website.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default Unsubscribe;

